import {Info} from '@mui/icons-material'
import {Box, Typography} from '@mui/material'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {trackEvent} from '../../../../common/analytics'

import {CustomSelect, SplitLoadMaterialList} from './components'
import {
  Contract,
  MaterialType,
  Plant,
  SplitLoadMaterial,
  SplitLoadMaterialSelectionProps
} from './types'
import {calculateDefaultQuantity, validateSplitLoadQuantities} from './utils/quantityValidation'

export const SplitLoadMaterialsSelection: React.FC<SplitLoadMaterialSelectionProps> = ({
  availablePlants,
  availableContracts,
  availableMaterials: allAvailableMaterials,
  initialData,
  onMaterialsSelected,
  resetDeliveryDateTime,
  slotExternalReference
}) => {
  // Track if component has been initialized from saved data
  const isInitializedRef = useRef(false)
  const {t} = useTranslation()

  // State - Updated order: Packaging -> Plant -> Contract
  const [selectedMaterialType, setSelectedMaterialType] = useState<MaterialType | null>(null)
  const [selectedPlant, setSelectedPlant] = useState<Plant | null>(null)
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null)
  const [selectedMaterials, setSelectedMaterials] = useState<SplitLoadMaterial[]>([])

  // Initialize from saved data on mount
  useEffect(() => {
    // Only initialize once when component mounts with initialData
    if (initialData && !isInitializedRef.current) {
      // Populate all state from saved data in the correct order
      setSelectedMaterialType(initialData.materialType)
      setSelectedPlant(initialData.plant)
      setSelectedContract(initialData.contract)
      setSelectedMaterials(initialData.materials)

      isInitializedRef.current = true
    }
  }, [initialData])

  // Get available materials based on all selections
  // Step 1: Filter by packaging type
  const materialsByPackaging = useMemo(() => {
    if (!selectedMaterialType) return []
    return allAvailableMaterials.filter((m) => m.materialType === selectedMaterialType.code)
  }, [allAvailableMaterials, selectedMaterialType])

  // Step 2: Filter by plant
  const materialsByPlant = useMemo(() => {
    if (!selectedPlant) return materialsByPackaging
    return materialsByPackaging.filter((m) => m.plant.plantNumber === selectedPlant.plantNumber)
  }, [materialsByPackaging, selectedPlant])

  // Step 3: Filter by contract (final available materials)
  const availableMaterials = useMemo(() => {
    if (!selectedContract) return []
    return materialsByPlant.filter(
      (m) => m.contractNumber === selectedContract.contractNumber && !m.invalid
    )
  }, [materialsByPlant, selectedContract])

  // Quantity validation
  const validationResult = useMemo(() => {
    return validateSplitLoadQuantities(selectedMaterials)
  }, [selectedMaterials])

  // Handlers - Updated order: Packaging -> Plant -> Contract
  const handleMaterialTypeSelect = useCallback((event: any) => {
    const materialTypeCode = event.target.value
    // Create MaterialType object with required fields
    const materialType: MaterialType = {
      id: materialTypeCode,
      code: materialTypeCode as 'bagged' | 'bulk',
      name: materialTypeCode
    }

    if (materialType) {
      setSelectedMaterialType(materialType)
      // Reset dependent selections when packaging changes
      setSelectedPlant(null)
      setSelectedContract(null)
      setSelectedMaterials([])
      // Mark as initialized when user manually selects
      if (!isInitializedRef.current) {
        isInitializedRef.current = true
      }

      trackEvent('hubOrderIntakeSelectSplitLoadPackaging', {
        packagingType: materialType.code
      })
    }
  }, [])

  const handlePlantSelect = useCallback(
    (event: any) => {
      const plantNumber = event.target.value
      const plant = availablePlants.find((p) => p.plantNumber === plantNumber)
      if (plant) {
        setSelectedPlant(plant)
        // Reset dependent selections when plant changes
        setSelectedContract(null)
        setSelectedMaterials([])

        trackEvent('hubOrderIntakeSelectSplitLoadPlant', {
          plantNumber: plant.plantNumber,
          plantName: plant.plantName
        })
        // Reset delivery date/time and release slot when plant changes
        slotExternalReference && resetDeliveryDateTime(true)
      }
    },
    [availablePlants, resetDeliveryDateTime, slotExternalReference]
  )

  const handleContractSelect = useCallback(
    (event: any) => {
      const contractNumber = event.target.value
      const contract = availableContracts.find((c) => c.contractNumber === contractNumber)
      if (contract) {
        setSelectedContract(contract)

        trackEvent('hubOrderIntakeSelectSplitLoadContract', {
          contractNumber: contract.contractNumber
        })

        // Reset materials when contract changes
        setSelectedMaterials([])

        // Get available materials for this contract
        const materials = allAvailableMaterials.filter(
          (m) =>
            m.materialType === selectedMaterialType?.code &&
            m.plant.plantNumber === selectedPlant?.plantNumber &&
            m.contractNumber === contractNumber &&
            !m.invalid
        )

        // If no valid materials, notify parent immediately
        if (materials.length === 0) {
          onMaterialsSelected?.({
            plant: selectedPlant!,
            contract: contract,
            materialType: selectedMaterialType!,
            materials: []
          })
          return
        }

        // If only one material is available, duplicate it to have 2 materials
        // Otherwise take first 2 materials
        let availableItems: typeof materials
        if (materials.length === 1) {
          const firstMaterial = materials[0]
          // Create a duplicate with unique id
          const duplicateMaterial = {
            ...firstMaterial,
            id: `${firstMaterial.id}-duplicate-${Date.now()}`
          }
          availableItems = [firstMaterial, duplicateMaterial]
        } else {
          availableItems = materials.slice(0, 2)
        }

        // Get truck capacity from first material to calculate default quantities
        const truckCapacity = availableItems[0]?.truckCapacity

        if (!truckCapacity) {
          return
        }

        // Set default quantities using validation logic
        const defaultMaterials = availableItems.map((m, index) => {
          const currentMaterials = availableItems.slice(0, index)
          const defaultQuantity = calculateDefaultQuantity(currentMaterials, truckCapacity)

          return {
            ...m,
            capacity: {...m.capacity, quantity: defaultQuantity}
          }
        })

        setSelectedMaterials(defaultMaterials)

        // Auto-save immediately if we have valid selections
        if (
          defaultMaterials.length >= 2 &&
          selectedPlant &&
          selectedMaterialType &&
          onMaterialsSelected &&
          isInitializedRef.current
        ) {
          onMaterialsSelected({
            plant: selectedPlant,
            contract: contract,
            materialType: selectedMaterialType,
            materials: defaultMaterials
          })
        }
      }
    },
    [
      availableContracts,
      allAvailableMaterials,
      selectedMaterialType,
      selectedPlant,
      onMaterialsSelected
    ]
  )

  // Helper function to save current state to parent
  const saveToParent = useCallback(
    (materials: SplitLoadMaterial[]) => {
      if (
        materials.length >= 2 &&
        selectedPlant &&
        selectedContract &&
        selectedMaterialType &&
        onMaterialsSelected
      ) {
        onMaterialsSelected({
          plant: selectedPlant,
          contract: selectedContract,
          materialType: selectedMaterialType,
          materials
        })
      }
    },
    [selectedPlant, selectedContract, selectedMaterialType, onMaterialsSelected]
  )

  const handleAddMaterial = useCallback(
    (material: SplitLoadMaterial) => {
      // Get truck capacity from first material
      const truckCapacity = selectedMaterials[0]?.truckCapacity
      if (!truckCapacity) return

      // Calculate default quantity for the new material
      const defaultQuantity = calculateDefaultQuantity(selectedMaterials, truckCapacity)

      // Create a new instance with a unique id to allow duplicate materials
      const newMaterial = {
        ...material,
        id: `${material.id}-${Date.now()}`, // Make id unique for duplicates
        capacity: {...material.capacity, quantity: defaultQuantity}
      }

      trackEvent('hubOrderIntakeAddSplitLoadMaterial', {
        materialDescription: newMaterial.materialDescription
      })

      const updatedMaterials = [...selectedMaterials, newMaterial]
      setSelectedMaterials(updatedMaterials)
      saveToParent(updatedMaterials)
    },
    [selectedMaterials, saveToParent]
  )

  const handleRemoveMaterial = useCallback(
    (index: number) => {
      const removedMaterial = selectedMaterials[index]

      const updatedMaterials = selectedMaterials.filter((m) => m !== removedMaterial)
      setSelectedMaterials(updatedMaterials)
      saveToParent(updatedMaterials)

      trackEvent('hubOrderIntakeRemoveSplitLoadMaterial', {
        materialDescription: removedMaterial.materialDescription
      })
    },
    [selectedMaterials, saveToParent]
  )

  const handleUpdateMaterial = useCallback(
    (index: number, newMaterial: SplitLoadMaterial) => {
      const updatedMaterials = selectedMaterials.map((m, i) => (i === index ? newMaterial : m))
      setSelectedMaterials(updatedMaterials)
      saveToParent(updatedMaterials)
      if (index === 0) slotExternalReference && resetDeliveryDateTime()
    },
    [selectedMaterials, saveToParent, slotExternalReference, resetDeliveryDateTime]
  )

  // Get unique material types from all available materials (packaging is first, no dependencies)
  const materialTypes = useMemo(() => {
    const types = new Set<string>()
    allAvailableMaterials.forEach((m) => types.add(m.materialType))
    return Array.from(types)
  }, [allAvailableMaterials])

  // Get unique plants from materials filtered by packaging type
  const availablePlantsForPackaging = useMemo(() => {
    if (!selectedMaterialType) return []

    const plantMap = new Map<string, Plant>()
    materialsByPackaging.forEach((m) => {
      if (!m.invalid && !plantMap.has(m.plant.plantNumber)) {
        // Only include plants that are in the availablePlants list
        const plant = availablePlants.find((p) => p.plantNumber === m.plant.plantNumber)
        if (plant) {
          plantMap.set(m.plant.plantNumber, plant)
        }
      }
    })
    return Array.from(plantMap.values())
  }, [materialsByPackaging, selectedMaterialType, availablePlants])

  // Get unique contracts from materials filtered by packaging and plant
  const availableContractsForPlant = useMemo(() => {
    if (!selectedPlant) return []

    const contractMap = new Map<string, Contract>()
    materialsByPlant.forEach((m) => {
      if (!m.invalid && !contractMap.has(m.contractNumber)) {
        const contract = availableContracts.find((c) => c.contractNumber === m.contractNumber)
        if (contract) {
          contractMap.set(m.contractNumber, contract)
        }
      }
    })

    return Array.from(contractMap.values())
  }, [materialsByPlant, selectedPlant, availableContracts])

  // Prepare options for dropdowns - Updated order: Packaging -> Plant -> Contract
  const materialTypeOptions = useMemo(
    () =>
      materialTypes.map((type) => ({
        value: type,
        label: t(`orderIntake.splitLoad.materialTypes.${type}`)
      })),
    [materialTypes, t]
  )

  const plantOptions = useMemo(
    () =>
      availablePlantsForPackaging.map((plant) => ({
        value: plant.plantNumber,
        label: plant.plantName
      })),
    [availablePlantsForPackaging]
  )

  const contractOptions = useMemo(
    () =>
      availableContractsForPlant.map((contract) => ({
        value: contract.contractNumber,
        label: contract.contractNumber
      })),
    [availableContractsForPlant]
  )

  // Determine if we should show warning (user has already made selections and has materials)
  const hasMaterialsSelected = selectedMaterials.length >= 2

  return (
    <Box sx={{mb: 2}}>
      {/* Section header */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 2}}>
        <Typography variant="h3" color="text.secondary">
          {t('orderIntake.splitLoad.materialSource')}
        </Typography>
      </Box>

      {/* Helper Text with Info Icon */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 1}}>
        <Typography variant="body2" color="text.secondary">
          {t('orderIntake.splitLoad.fillFieldsHelper')}
        </Typography>
        <Info sx={{fontSize: 18, color: 'info.main'}} />
      </Box>

      {/* Material Type (Packaging) Dropdown - FIRST */}
      <CustomSelect
        label={t('orderIntake.splitLoad.packaging')}
        value={selectedMaterialType?.code || ''}
        disabled={false}
        options={materialTypeOptions}
        onChange={handleMaterialTypeSelect}
        showWarning={hasMaterialsSelected}
        warningMessage={t('orderIntake.splitLoad.changeWarning')}
        data-test-id="split-load-packaging-select"
      />

      {/* Plant Dropdown - SECOND */}
      <CustomSelect
        label={t('orderIntake.splitLoad.plant')}
        value={selectedPlant?.plantNumber || ''}
        disabled={!selectedMaterialType}
        disabledPlaceholder={t('orderIntake.splitLoad.selectPackagingFirst')}
        options={plantOptions}
        onChange={handlePlantSelect}
        showWarning={hasMaterialsSelected}
        warningMessage={t('orderIntake.splitLoad.changeWarning')}
        data-test-id="split-load-plant-select"
      />

      {/* Contract Dropdown - THIRD */}
      <CustomSelect
        label={t('orderIntake.splitLoad.contract')}
        value={selectedContract?.contractNumber || ''}
        disabled={!selectedPlant}
        disabledPlaceholder={t('orderIntake.splitLoad.selectPlantFirst')}
        options={contractOptions}
        onChange={handleContractSelect}
        showWarning={hasMaterialsSelected}
        warningMessage={t('orderIntake.splitLoad.changeWarning')}
        data-test-id="split-load-contract-select"
      />

      {/* Material Rows - Display directly when packaging is selected */}
      {
        <SplitLoadMaterialList
          selectedMaterials={selectedMaterials}
          availableMaterials={availableMaterials}
          validationResult={validationResult}
          hasSourceSelected={!!(selectedMaterialType && selectedPlant && selectedContract)}
          onUpdateMaterial={handleUpdateMaterial}
          onRemoveMaterial={handleRemoveMaterial}
          onAddMaterial={handleAddMaterial}
          data-test-id="split-load-material-list"
        />
      }
    </Box>
  )
}
